import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { Slider } from '@/components/ui/slider';

interface PublicAssessmentRecordingProps {
  assessmentTitle: string;
  audioUrl: string;
  isPlaying: boolean;
  isMuted: boolean;
  currentTime: number;
  duration: number;
  handlePlayPause: () => void;
  handleVolumeToggle: () => void;
  handleSeek: (value: number[]) => void;
  setDuration: (duration: number) => void;
  setCurrentTime: (time: number) => void;
}

const PublicAssessmentRecording: React.FC<PublicAssessmentRecordingProps> = ({
  assessmentTitle,
  audioUrl,
  isPlaying,
  isMuted,
  currentTime,
  duration,
  handlePlayPause,
  handleVolumeToggle,
  handleSeek,
  setDuration,
  setCurrentTime,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioUrl) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.muted = isMuted;
      
      audioRef.current.addEventListener('loadedmetadata', () => {
        if (audioRef.current) {
          // Update duration when metadata is loaded
          const audioDuration = audioRef.current.duration;
          if (!isNaN(audioDuration)) {
            setDuration(audioDuration);
          }
          audioRef.current.currentTime = 0;
          setCurrentTime(0);
        }
      });

      // We're removing the timeupdate event to prevent circular updates
      
      audioRef.current.addEventListener('ended', () => {
        handlePlayPause();
      });

      return () => {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      };
    }
  }, [audioUrl]);

  // Add a time update interval when playing
  useEffect(() => {
    let timeUpdateInterval: number | null = null;
    
    if (isPlaying && audioRef.current) {
      timeUpdateInterval = window.setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 100);
    }
    
    return () => {
      if (timeUpdateInterval !== null) {
        clearInterval(timeUpdateInterval);
      }
    };
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Keep audio element and UI state in sync
  useEffect(() => {
    if (audioRef.current && Math.abs(audioRef.current.currentTime - currentTime) > 0.5) {
      audioRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Assessment Recording</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePlayPause}
            className="h-8 w-8"
          >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleVolumeToggle}
            className="h-8 w-8"
          >
            {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </Button>
          <div className="text-sm text-muted-foreground">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        <div className="px-1">
          <Slider
            value={[currentTime]}
            min={0}
            max={duration}
            step={1}
            onValueChange={(value) => {
              if (audioRef.current && value.length > 0) {
                const newTime = value[0];
                audioRef.current.currentTime = newTime;
                setCurrentTime(newTime);
              }
            }}
            className="w-full"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default PublicAssessmentRecording; 